<template>
  <v-container class="py-12">
    <v-row>
      <v-col cols="12">
        <div class="title-container">
          <div class="title-block">
            <span id="title" class="page-title">Manutenções Preventivas</span>
            <v-breadcrumbs class="app-breadcrumb" divider="›" :items="[
              { text: 'Home', disabled: false, to: '/home', exact: true },
              { text: 'Manutenções', disabled: false, to: '/manutencoes', exact: true },
              { text: 'Preventivas', disabled: true, exact: true },
            ]"></v-breadcrumbs>
          </div>
          <div class="button-group">
            <v-btn outlined color="primary" elevation="0" to="/manutencoes/programacoes">
              <v-icon left small>mdi-cog-outline</v-icon>Programações
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <div class="stat-grid">
      <stat-card label="Vencidas" icon="mdi-alert-circle-outline" :value="summary.overdue" color="#d32f2f"
        :alert="summary.overdue > 0" clickable hint="itens com prazo vencido" @click="quickFilter('overdue')" />
      <stat-card label="Próximas" icon="mdi-clock-alert-outline" :value="summary.due_soon" color="#ef6c00"
        clickable hint="dentro do aviso" @click="quickFilter('due_soon')" />
      <stat-card label="Sem referência" icon="mdi-help-circle-outline" :value="summary.no_reference"
        clickable hint="sem última troca registrada" @click="quickFilter('no_reference')" />
      <stat-card label="KM desatualizado" icon="mdi-speedometer-slow" :value="summary.stale_vehicles"
        clickable :hint="`veículos sem abastecimento há mais de 7 dias`" @click="quickFilter('stale')" />
      <stat-card label="Em dia" icon="mdi-check-circle-outline" :value="summary.ok" color="#2e7d32"
        clickable :hint="`${summary.vehicles} veículo(s) acompanhados`" @click="quickFilter('ok')" />
    </div>

    <v-card class="filter-card">
      <v-card-title class="filter-card-title">Filtros de busca</v-card-title>
      <v-card-text>
        <v-form @submit.prevent="applyFilter">
          <v-row>
            <v-col cols="12" sm="6" md="2">
              <v-text-field dense outlined hide-details label="Frota" v-model="filter.code" clearable
                @click:clear="filter.code = ''" />
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-text-field dense outlined hide-details label="Placa" v-model="filter.plate" clearable
                @click:clear="filter.plate = ''" />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-autocomplete dense outlined hide-details label="Item" :items="schedulings" item-text="name"
                item-value="id" v-model="filter.scheduling_id" clearable />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select dense outlined hide-details label="Situação" :items="statusOptions" v-model="filter.status"
                clearable />
            </v-col>
            <v-col cols="12" md="auto">
              <v-btn id="btnFilter" type="submit" color="primary" elevation="0">Aplicar filtros</v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <v-card>
      <v-data-table :headers="headers" :items="items" :server-items-length="total" :loading="loading"
        :options.sync="options" :footer-props="footerOptions" disable-sort class="app-table"
        no-data-text="Nenhum item de manutenção encontrado" loading-text="Carregando dados..."
        @update:options="load">
        <template v-slot:[`item.vehicle`]="{ item }">
          <div class="d-flex align-center">
            <span class="status-bar" :style="{ background: statusColor(item.situation) }"></span>
            <div>
              <div class="font-weight-bold">{{ item.fleetCode ? `Frota ${item.fleetCode}` : item.plate }}</div>
              <div class="caption grey--text text--darken-1">{{ item.plate }} · {{ [item.brand, item.model].filter(Boolean).join(' ') || 'Sem modelo' }}</div>
            </div>
          </div>
        </template>

        <template v-slot:[`item.schedulingName`]="{ item }">
          <div class="font-weight-medium">{{ item.schedulingName }}</div>
          <div class="caption grey--text text--darken-1">
            {{ item.maintenanceIntervalLabel || 'Sem intervalo' }}
            <span v-if="item.intervalSource === 'model'" class="source-tag">modelo</span>
            <span v-else-if="item.intervalSource === 'vehicle'" class="source-tag source-tag--vehicle">veículo</span>
          </div>
        </template>

        <template v-slot:[`item.km`]="{ item }">
          <template v-if="item.triggerType === 'odometer'">
            <div class="text-no-wrap">{{ km(item.km) }}</div>
            <div class="caption text-no-wrap" :class="item.odometerStale ? 'orange--text text--darken-3' : 'grey--text'">
              <v-icon v-if="item.odometerStale" x-small color="orange darken-3">mdi-alert-outline</v-icon>
              {{ item.lastReadingAt ? `leitura ${date(item.lastReadingAt)}` : 'sem abastecimento' }}
            </div>
          </template>
          <span v-else class="grey--text">-</span>
        </template>

        <template v-slot:[`item.reference`]="{ item }">
          <template v-if="hasReference(item)">
            <div class="text-no-wrap">{{ item.triggerType === 'odometer' ? km(item.referenceKm) : date(item.referenceDate) }}</div>
            <div v-if="item.triggerType === 'odometer' && item.referenceDate" class="caption grey--text">{{ date(item.referenceDate) }}</div>
          </template>
          <span v-else class="grey--text">-</span>
        </template>

        <template v-slot:[`item.next`]="{ item }">
          <span v-if="item.targetKm !== null" class="text-no-wrap">{{ km(item.targetKm) }}</span>
          <span v-else-if="item.targetDate" class="text-no-wrap">{{ date(item.targetDate) }}</span>
          <span v-else class="grey--text">-</span>
        </template>

        <template v-slot:[`item.situation`]="{ item }">
          <div class="situation">
            <v-chip x-small label :color="statusColor(item.situation)" text-color="white" class="font-weight-bold">
              {{ item.situationLabel }}
            </v-chip>
            <div class="caption mt-1" :class="{ 'red--text': item.situation === 'overdue' }">{{ remainingLabel(item) }}</div>
            <v-progress-linear v-if="item.remaining !== null" :value="item.progress" :color="statusColor(item.situation)"
              height="4" rounded class="mt-1" />
          </div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click="openReference(item)">
                  <v-icon small>mdi-flag-outline</v-icon>
                </v-btn>
              </template>
              <span>Informar última troca / intervalo do veículo</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small color="primary" v-bind="attrs" v-on="on"
                  :to="{ path: '/manutencoes/preventiva/novo', query: { msv_id: item.maintenanceSchedulingVehicleId } }">
                  <v-icon small>mdi-wrench-outline</v-icon>
                </v-btn>
              </template>
              <span>Registrar manutenção</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="reference.open" max-width="520">
      <v-card v-if="reference.item">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          {{ reference.item.schedulingName }}
          <v-spacer />
          <v-btn icon small @click="reference.open = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-subtitle class="pb-2">
          {{ reference.item.fleetCode ? `Frota ${reference.item.fleetCode} · ` : '' }}{{ reference.item.plate }} ·
          {{ [reference.item.brand, reference.item.model].filter(Boolean).join(' ') }}
        </v-card-subtitle>
        <v-card-text>
          <v-form ref="referenceForm" @submit.prevent="saveReference">
            <div class="caption grey--text mb-3">
              Use quando a última troca foi feita antes do cadastro no sistema. Uma manutenção registrada
              depois substitui esta referência.
            </div>
            <v-row dense>
              <v-col v-if="reference.item.triggerType === 'odometer'" cols="12" sm="6">
                <v-text-field dense outlined type="number" min="0" label="KM da última troca" suffix="km"
                  v-model="reference.form.last_trigger_value" :rules="[$rules.required]" hide-details="auto"
                  :hint="reference.item.km !== null ? `KM atual: ${km(reference.item.km)}` : ''" persistent-hint />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field dense outlined type="date" label="Data da última troca"
                  v-model="reference.form.last_completed_at" hide-details="auto"
                  :rules="reference.item.triggerType === 'time' ? [$rules.required] : []" />
              </v-col>
            </v-row>
            <v-divider class="my-4" />
            <v-row dense align="center">
              <v-col cols="12" sm="7">
                <v-text-field dense outlined type="number" min="1" label="Intervalo próprio deste veículo"
                  :suffix="unitLabel(reference.item.maintenanceIntervalUnit)" v-model="reference.form.custom_interval_value"
                  :placeholder="inheritedInterval(reference.item)" persistent-placeholder hide-details="auto"
                  hint="Vazio: usa a regra do modelo ou o padrão do item" persistent-hint />
              </v-col>
              <v-col cols="12" sm="5">
                <v-btn text small color="primary" :disabled="!reference.form.custom_interval_value"
                  @click="reference.form.custom_interval_value = ''">Usar padrão</v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="reference.open = false">Cancelar</v-btn>
          <v-btn color="primary" depressed :loading="reference.saving" @click="saveReference">Salvar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

const STATUS_COLORS = {
  overdue: '#d32f2f',
  due_soon: '#ef6c00',
  no_reference: '#7b8aa3',
  no_odometer: '#9e9e9e',
  ok: '#2e7d32',
  not_tracked: '#b0bac9',
}

const UNITS = { km: 'km', mi: 'mi', days: 'dias', hours: 'horas' }

export default {
  name: 'PreventiveList',

  data: () => ({
    loading: false,
    items: [],
    total: 0,
    summary: { overdue: 0, due_soon: 0, no_reference: 0, no_odometer: 0, ok: 0, stale_vehicles: 0, vehicles: 0 },
    schedulings: [],
    filter: { code: '', plate: '', scheduling_id: null, status: null },
    options: { page: 1, itemsPerPage: 25 },
    footerOptions: {
      itemsPerPageText: 'Itens por página:',
      itemsPerPageOptions: [25, 50, 100],
      pageText: '{0}-{1} de {2}',
    },
    statusOptions: [
      { value: 'overdue,due_soon', text: 'Pendentes (vencidas e próximas)' },
      { value: 'overdue', text: 'Vencidas' },
      { value: 'due_soon', text: 'Próximas' },
      { value: 'no_reference', text: 'Sem referência' },
      { value: 'no_odometer', text: 'Sem KM' },
      { value: 'ok', text: 'Em dia' },
      { value: 'stale', text: 'KM desatualizado' },
    ],
    headers: [
      { text: 'Veículo', value: 'vehicle' },
      { text: 'Item', value: 'schedulingName' },
      { text: 'KM atual', value: 'km' },
      { text: 'Última troca', value: 'reference' },
      { text: 'Próxima', value: 'next' },
      { text: 'Situação', value: 'situation', width: 190 },
      { text: '', value: 'actions' },
    ],
    reference: { open: false, item: null, saving: false, form: {} },
  }),

  created() {
    this.loadSchedulings()
  },

  methods: {
    applyFilter() {
      if (this.options.page !== 1) {
        this.options = { ...this.options, page: 1 }
        return
      }
      this.load()
    },

    quickFilter(status) {
      this.filter.status = status
      this.applyFilter()
    },

    async loadSchedulings() {
      try {
        const response = await axios.get('/api/v2/maintenance/schedulings', { params: { page: 1, limit: 500 } })
        this.schedulings = (response.data.content.data || []).filter((item) => item.maintenance_type === 1 || !item.maintenance_type)
      } catch (error) {
        this.schedulings = []
      }
    },

    async load() {
      const params = {
        page: this.options.page,
        limit: this.options.itemsPerPage,
        code: this.filter.code || undefined,
        plate: this.filter.plate || undefined,
        scheduling_id: this.filter.scheduling_id || undefined,
      }
      if (this.filter.status === 'stale') params.stale = true
      else if (this.filter.status) params.status = this.filter.status

      this.loading = true
      try {
        const response = await axios.get('/api/v2/maintenance/preventive', { params })
        const content = response.data.content || {}
        this.items = content.data || []
        this.total = content.total || 0
        this.summary = content.summary || this.summary
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.loading = false
      }
    },

    openReference(item) {
      this.reference = {
        open: true,
        saving: false,
        item,
        form: {
          last_trigger_value: item.referenceKm !== null ? item.referenceKm : '',
          last_completed_at: item.referenceDate ? moment(item.referenceDate).format('YYYY-MM-DD') : '',
          custom_interval_value: item.customIntervalValue || '',
        },
      }
    },

    async saveReference() {
      if (!this.$refs.referenceForm.validate()) return

      const form = this.reference.form
      const payload = {
        last_trigger_value: form.last_trigger_value === '' || form.last_trigger_value === null ? null : Number(form.last_trigger_value),
        last_completed_at: form.last_completed_at || null,
        custom_interval_value: form.custom_interval_value ? Number(form.custom_interval_value) : null,
      }

      this.reference.saving = true
      try {
        await axios.patch(`/api/v2/maintenance/preventive/${this.reference.item.maintenanceSchedulingVehicleId}/reference`, payload)
        this.$dialog.notify.success('Referência salva', { position: 'top-right', timeout: 2500 })
        this.reference.open = false
        await this.load()
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.reference.saving = false
      }
    },

    hasReference(item) {
      return item.triggerType === 'odometer' ? item.referenceKm !== null : Boolean(item.referenceDate)
    },

    inheritedInterval(item) {
      const value = item.modelIntervalValue || item.defaultIntervalValue
      if (!value) return ''
      const source = item.modelIntervalValue ? 'modelo' : 'padrão'
      return `${Number(value).toLocaleString('pt-BR')} (${source})`
    },

    remainingLabel(item) {
      if (item.situation === 'no_reference') return 'Informe a última troca'
      if (item.situation === 'no_odometer') return 'Veículo sem KM'
      if (item.remaining === null) return ''
      const unit = item.remainingUnit === 'days' ? 'dia(s)' : 'km'
      const value = Math.abs(item.remaining).toLocaleString('pt-BR')
      return item.remaining > 0 ? `Faltam ${value} ${unit}` : `Passou ${value} ${unit}`
    },

    statusColor(status) {
      return STATUS_COLORS[status] || '#b0bac9'
    },

    unitLabel(unit) {
      return UNITS[unit] || unit || ''
    },

    km(value) {
      if (value === null || value === undefined) return '-'
      return `${Number(value).toLocaleString('pt-BR')} km`
    },

    date(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '-'
    },

    notifyError(error) {
      this.$dialog.notify.error(this.$codes ? this.$codes(error) : 'Não foi possível carregar as manutenções.', {
        position: 'top-right',
        timeout: 5000,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.status-bar {
  flex: 0 0 4px;
  align-self: stretch;
  min-height: 34px;
  margin-right: 10px;
  border-radius: 2px;
}

.situation {
  min-width: 150px;
}

.source-tag {
  margin-left: 4px;
  padding: 0 5px;
  border-radius: 4px;
  background: #e8f0fe;
  color: #1450b5;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
}

.source-tag--vehicle {
  background: #fff3e0;
  color: #b25400;
}
</style>
